import { ActionIcon, Button, Group, Paper, Text, Tooltip } from "@mantine/core";
import { IconPencil, IconRefresh, IconTrash } from "@tabler/icons-react";
import type { ReactNode } from "react";

import { translateExternalSystem } from "../../../common/Dictionary";
import type { ExternalSystemSettingsResponseDto } from "../../../generated/client/octalog-service";
import { ExternalSystem } from "../../../generated/client/octalog-service";

interface ExternalSystemSettingItemProps {
  /** The external system setting data to display. */
  setting: ExternalSystemSettingsResponseDto;
  /** Callback function triggered when the edit action is requested. */
  onEdit: (setting: ExternalSystemSettingsResponseDto) => void;
  /** Callback function triggered when the delete action is requested. */
  onDelete: (settingId: string) => void;
  /** Callback function triggered when the sync action is requested. */
  onTriggerSync: (setting: ExternalSystemSettingsResponseDto) => void;
}

/**
 * Displays a single external system setting and provides actions
 * like editing, deleting, or triggering synchronization.
 */
export function ExternalSystemSettingItem({
  setting,
  onEdit,
  onDelete,
  onTriggerSync,
}: ExternalSystemSettingItemProps): ReactNode {
  const isLexware = setting.system === ExternalSystem.LEXWARE;
  const hasToken = setting.tokenLength > 0;
  const syncDisabled = !isLexware || !hasToken;
  const syncTooltipLabel = isLexware
    ? hasToken
      ? "Kontakte von Lexware zu Octalog synchronisieren"
      : "Kein Token konfiguriert"
    : "Synchronisation nur für Lexware verfügbar";

  return (
    <Paper p="md" shadow="xs" withBorder>
      <Group justify="space-between">
        <Group>
          <Text fw={500}>{translateExternalSystem(setting.system)}</Text>
          <Text c="dimmed" size="sm">
            Token konfiguriert (Länge: {setting.tokenLength})
          </Text>
        </Group>
        <Group>
          <Tooltip label="Token bearbeiten">
            <ActionIcon color="blue" onClick={() => onEdit(setting)} variant="subtle">
              <IconPencil size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={syncTooltipLabel}>
            <span>
              <Button
                disabled={syncDisabled}
                leftSection={<IconRefresh size={16} />}
                onClick={() => onTriggerSync(setting)}
                size="xs"
                variant="light"
              >
                Kontakte Synchronisieren
              </Button>
            </span>
          </Tooltip>
          <Tooltip label="Token löschen">
            <ActionIcon color="red" onClick={() => onDelete(setting.id)} variant="subtle">
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Paper>
  );
}
